/**
 * Walkthrough engine — route-driven provider + spotlight/tooltip overlay.
 *
 * - Auto-plays a surface's tour once per (user, tour id, tour version),
 *   after a short settle delay so tooltips never point at skeletons.
 * - Complete / skip / dismiss / navigate-away all mark the tour seen; the
 *   user can reset or replay from Account → Guides & walkthroughs.
 * - Hand-rolled per PRD §6.3 — no dependencies, native to the design system.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { clearFlag, readFlag, writeFlag } from './flags'
import { TOURS, tourById, tourForRoute, tourVersionKey, type Tour, type TourStep } from './registry'

const AUTO_DELAY_MS = 600
const REPLAY_DELAY_MS = 350
const SPOTLIGHT_PAD = 6
const TIP_GAP = 14
const TIP_WIDTH = 320

interface ActiveTour {
  tour: Tour
  /** Steps with missing targets already filtered out ('center' fallbacks kept). */
  steps: TourStep[]
  step: number
}

interface ToursContextValue {
  activeTourId: string | null
  isSeen: (tour: Tour) => boolean
  /** Replay a tour now — navigates to its route first if needed. */
  playTour: (id: string) => void
  resetTour: (id: string) => void
  resetAllTours: () => void
}

const ToursContext = createContext<ToursContextValue | null>(null)

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Steps whose target exists render anchored; 'center' fallbacks always survive. */
function playableSteps(tour: Tour): TourStep[] {
  return tour.steps.filter(
    (s) => s.fallback === 'center' || document.querySelector(`[data-tour="${s.target}"]`),
  )
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const location = useLocation()
  const navigate = useNavigate()

  const [active, setActive] = useState<ActiveTour | null>(null)
  const [seen, setSeen] = useState<Record<string, string>>({})
  const pendingReplayRef = useRef<string | null>(null)
  const pathnameRef = useRef(location.pathname)
  pathnameRef.current = location.pathname

  // Load this user's flags (per-UID; changes on sign-in/out).
  useEffect(() => {
    if (!uid) {
      setSeen({})
      return
    }
    const next: Record<string, string> = {}
    for (const t of TOURS) {
      const ts = readFlag(uid, t.id, t.version)
      if (ts) next[tourVersionKey(t)] = ts
    }
    setSeen(next)
  }, [uid])

  const markSeen = useCallback(
    (tour: Tour) => {
      if (!uid) return
      const ts = writeFlag(uid, tour.id, tour.version)
      setSeen((prev) => ({ ...prev, [tourVersionKey(tour)]: ts }))
    },
    [uid],
  )

  /** Close the tour; any end state (done, skip, dismiss, navigate) marks it seen. */
  const finish = useCallback(
    (current: ActiveTour) => {
      markSeen(current.tour)
      setActive(null)
    },
    [markSeen],
  )

  const begin = useCallback((tour: Tour) => {
    const steps = playableSteps(tour)
    if (!steps.length) return
    setActive({ tour, steps, step: 0 })
  }, [])

  const playTour = useCallback(
    (id: string) => {
      const tour = tourById(id)
      if (!tour) return
      if (pathnameRef.current === tour.route) {
        begin(tour)
      } else {
        pendingReplayRef.current = id
        navigate(tour.route)
      }
    },
    [begin, navigate],
  )

  const resetTour = useCallback(
    (id: string) => {
      const tour = tourById(id)
      if (!uid || !tour) return
      clearFlag(uid, tour.id, tour.version)
      setSeen((prev) => {
        const next = { ...prev }
        delete next[tourVersionKey(tour)]
        return next
      })
    },
    [uid],
  )

  const resetAllTours = useCallback(() => {
    for (const t of TOURS) resetTour(t.id)
  }, [resetTour])

  // Auto-play: first visit per (user, tour version), after the page settles.
  useEffect(() => {
    if (!uid || active) return
    const tour = tourForRoute(location.pathname)
    if (!tour || seen[tourVersionKey(tour)]) return
    const timer = window.setTimeout(() => begin(tour), AUTO_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [uid, active, location.pathname, seen, begin])

  // Replay requested from another page (Account) — start once the route lands.
  useEffect(() => {
    const id = pendingReplayRef.current
    if (!id) return
    const tour = tourById(id)
    if (!tour) {
      pendingReplayRef.current = null
      return
    }
    if (tour.route !== location.pathname) return
    pendingReplayRef.current = null
    const timer = window.setTimeout(() => begin(tour), REPLAY_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [location.pathname, begin])

  // Navigating away mid-tour dismisses it (marks seen — never ambush twice).
  useEffect(() => {
    if (active && active.tour.route !== location.pathname) finish(active)
  }, [active, location.pathname, finish])

  const value = useMemo<ToursContextValue>(
    () => ({
      activeTourId: active?.tour.id ?? null,
      isSeen: (tour: Tour) => Boolean(seen[tourVersionKey(tour)]),
      playTour,
      resetTour,
      resetAllTours,
    }),
    [active, seen, playTour, resetTour, resetAllTours],
  )

  return (
    <ToursContext.Provider value={value}>
      {children}
      {active ? (
        <TourOverlay
          active={active}
          onStep={(step) => setActive((a) => (a ? { ...a, step } : a))}
          onFinish={() => finish(active)}
        />
      ) : null}
    </ToursContext.Provider>
  )
}

export function useTours(): ToursContextValue {
  const ctx = useContext(ToursContext)
  if (!ctx) throw new Error('useTours requires TourProvider')
  return ctx
}

/* ------------------------------------------------------------------ */
/* Overlay: dimmed backdrop, gliding spotlight, tooltip card.          */
/* ------------------------------------------------------------------ */

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

function TourOverlay({
  active,
  onStep,
  onFinish,
}: {
  active: ActiveTour
  onStep: (step: number) => void
  onFinish: () => void
}) {
  const { steps, step } = active
  const current = steps[step]
  const isLast = step === steps.length - 1

  const [rect, setRect] = useState<Rect | null>(null)
  const [tipHeight, setTipHeight] = useState(180)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  const next = useCallback(() => {
    if (isLast) onFinish()
    else onStep(step + 1)
  }, [isLast, onFinish, onStep, step])
  const prev = useCallback(() => onStep(Math.max(0, step - 1)), [onStep, step])

  // Measure the anchor element; keep the spotlight glued on scroll/resize.
  useLayoutEffect(() => {
    const el = document.querySelector(`[data-tour="${current.target}"]`)
    if (!el) {
      setRect(null)
      return
    }
    const reduced = prefersReducedMotion()
    el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' })
    let raf = 0
    const measure = () => {
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    const timer = window.setTimeout(measure, reduced ? 30 : 320)
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    window.addEventListener('resize', onScroll)
    window.addEventListener('scroll', onScroll, { capture: true })
    return () => {
      window.clearTimeout(timer)
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [current.target])

  // Keyboard: Esc dismisses, arrows navigate, Tab stays inside the card.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onFinish()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prev()
      } else if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        )
        if (!focusables.length) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [next, prev, onFinish])

  // Move focus into the card on each step; track height for placement.
  useLayoutEffect(() => {
    dialogRef.current?.focus()
    if (dialogRef.current) setTipHeight(dialogRef.current.getBoundingClientRect().height)
  }, [step, rect])

  const width = Math.min(TIP_WIDTH, window.innerWidth - 24)
  let tipStyle: CSSProperties
  let centered = false
  if (rect) {
    const below = rect.top + rect.height + SPOTLIGHT_PAD + TIP_GAP
    const fitsBelow = below + tipHeight < window.innerHeight - 12
    const top = fitsBelow
      ? below
      : Math.max(12, rect.top - SPOTLIGHT_PAD - TIP_GAP - tipHeight)
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12)
    tipStyle = { top, left, width }
  } else {
    centered = true
    tipStyle = { top: '50%', left: '50%', width, transform: 'translate(-50%, -50%)' }
  }

  return (
    <div className="tour-layer">
      <div
        className={rect ? 'tour-backdrop' : 'tour-backdrop dim'}
        onClick={onFinish}
        aria-hidden="true"
      />
      {rect ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - SPOTLIGHT_PAD,
            left: rect.left - SPOTLIGHT_PAD,
            width: rect.width + SPOTLIGHT_PAD * 2,
            height: rect.height + SPOTLIGHT_PAD * 2,
          }}
          aria-hidden="true"
        />
      ) : null}
      <div
        ref={dialogRef}
        className={centered ? 'tour-tooltip centered' : 'tour-tooltip'}
        style={tipStyle}
        role="dialog"
        aria-modal="true"
        aria-label={current.title}
        tabIndex={-1}
      >
        <span className="tour-step-count">
          {active.tour.title} · {step + 1} of {steps.length}
        </span>
        <h2 className="tour-title">{current.title}</h2>
        <p className="tour-body">{current.body}</p>
        <div className="tour-footer">
          <span className="tour-dots" aria-hidden="true">
            {steps.map((s, i) => (
              <span key={s.target} className={i === step ? 'tour-dot on' : 'tour-dot'} />
            ))}
          </span>
          <span className="tour-nav">
            <button type="button" className="tour-skip" onClick={onFinish}>
              Skip tour
            </button>
            {step > 0 ? (
              <button type="button" className="ghost" onClick={prev}>
                Back
              </button>
            ) : null}
            <button type="button" className="login-primary" onClick={next}>
              {isLast ? 'Done' : 'Next'}
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
